import {
    QUERY_FILES_BY_FOLDER_PENDING,
    QUERY_FILES_BY_FOLDER_REJECTED,
    QUERY_FILES_BY_FOLDER_FULFILLED,
    SET_SEARCH_TEXT,
    NEW_FOLDER,
    SAVE_NEW_FOLDER_PENDING,
    SAVE_NEW_FOLDER_FULFILLED,
    SAVE_NEW_FOLDER_REJECTED,
    ABORT_NEW_FOLDER,
    DELETE_FOLDER_FULFILLED,
    DELETE_FILE_PENDING,
    DELETE_FILE_FULFILLED,
    SAVE_NEW_FILE_PENDING,
    SAVE_NEW_FILE_REJECTED,
    SAVE_NEW_FILE_FULFILLED,
    SELECT_ITEM,
    UPLOADED_FILE,
    SET_RENAMING_ITEM,
    UPDATE_FOLDER_FULFILLED,
    UPDATE_FILE_FULFILLED,
    SET_SELECTED_ITEMS,
    FETCH_SHAREPOINT_ATTACHED_FILES_FOR_PAGE_FULFILLED,
    FETCH_SHAREPOINT_ATTACHED_FILES_FOR_PAGE_PENDING,
} from "./file-action-types";
import moment from "moment";
import { localize } from "src/l10n";
import { fileTypeToName } from "./utils";

export interface IFileFileState {
    currentParams: IQueryParams;
    currentSource?: string;
    currentId?: string;

    items: any[];
    queryItems: IQueryItems;
    searchText: string;
    totalCount: number;
    isLoadingNewFile: boolean;
    selectedItems: Spintr.IFileBrowserFile[];
    sharePointAttachedFiles: ISharePointAttachedFiles;
}

interface IQueryParams {
    orderByColumn: string;
    isAscending: boolean;
    searchText: string;
}

interface IQueryItems {
    errors: IError;
    isLoading: boolean;
}

interface IError {
    exists: boolean;
}

interface ISharePointAttachedFiles {
    isLoading: boolean;
    searches: any[];
}

export const initialState: IFileFileState = {
    currentParams: { orderByColumn: "", isAscending: true, searchText: "" },
    items: [],
    queryItems: {
        errors: { exists: false },
        isLoading: false,
    },
    searchText: "",
    totalCount: 0,
    isLoadingNewFile: false,
    selectedItems: [],
    sharePointAttachedFiles: {
        // TODO: Move this? More related to pages than files
        isLoading: false,
        searches: [],
    },
};

const queryFilesByFolderPending = (state, action) => ({
    ...state,
    queryItems: {
        errors: initialState.queryItems.errors,
        folderId: action.meta.folderId,
        isLoading: true,
    },
});

const queryFilesByFolderRejected = (state) => ({
    ...state,
    queryItems: {
        ...state.queryItems,
        isLoading: false,
        errors: { exists: true },
    },
    items: [],
    totalCount: 0,
});

const queryFilesByFolderFulfilled = (state, action) => {
    let files = action.payload.data.items;

    const { folderId, source } = action.meta;
    const nextParams: IQueryParams = {
        isAscending: action.meta.isAscending,
        orderByColumn: action.meta.orderByColumn,
        searchText: action.meta.searchText || "",
    };

    files = files.map((file) => ({
        ...file,
        date: file.date ? moment(file.date).format("L") : null,
        typeName: file.isDirectory ? localize("Mapp") : localize(fileTypeToName[file.type], true),
    }));

    const paramsDidChange = (
        nextParams.isAscending !== state.currentParams.isAscending ||
        nextParams.orderByColumn !== state.currentParams.orderByColumn ||
        nextParams.searchText !== state.currentParams.searchText ||
        folderId !== state.currentFolderId ||
        source !== state.currentSource
    );

    return {
        ...state,
        items: paramsDidChange
            ? files
            : state.items.concat(
                  files.filter((f1) => !!f1 && state.items.every((f2) => f2.id !== f1.id)),
              ),
        queryItems: {
            ...state.queryItems,
            errors: {
                exists: false,
            },
            isLoading: false,
        },
        currentParams: nextParams,
        currentFolderId: folderId,
        currentSource: source,
        selectedItems: paramsDidChange ? initialState.selectedItems : state.selectedItems,

        totalCount: action.payload.data.totalCount == 0 && files.length > 0 && action.payload.data.hasMore
            ? state.items.length + 1
            : action.payload.data.totalCount,
    };
};

const fileReducer = (state = initialState, action) => {
    switch (action.type) {
        case QUERY_FILES_BY_FOLDER_PENDING:
            return queryFilesByFolderPending(state, action);

        case QUERY_FILES_BY_FOLDER_REJECTED:
            return queryFilesByFolderRejected(state);

        case QUERY_FILES_BY_FOLDER_FULFILLED:
            return queryFilesByFolderFulfilled(state, action);

        case SET_SEARCH_TEXT:
            return {
                ...state,
                items: [],
                searchText: action.payload,
                selectedItems: initialState.selectedItems,
            };

        case NEW_FOLDER:
            return {
                ...state,
                items: [{ isDirectory: true, newFolder: true }, ...state.items],
            };

        case ABORT_NEW_FOLDER:
            return {
                ...state,
                items: state.items.filter((i) => !i.newFolder),
            };

        case DELETE_FOLDER_FULFILLED:
            return {
                ...state,
                items: state.items.filter((i) => i.id != action.meta),
                selectedItems: initialState.selectedItems,
            };

        case DELETE_FILE_PENDING:
            return {
                ...state,
                queryItems: {
                    ...state.queryItems,
                    isLoading: true,
                },
            };

        case DELETE_FILE_FULFILLED:
            return {
                ...state,
                items: state.items.filter((i) => i.id != action.meta),
                selectedItems: initialState.selectedItems,
                queryItems: {
                    ...state.queryItems,
                    isLoading: false,
                },
            };

        case SAVE_NEW_FOLDER_PENDING:
            return {
                ...state,
                items: state.items.map((item) => item.newFolder
                    ? { ...item, isLoading: true, name: action.meta.name }
                    : item),
            };

        case SAVE_NEW_FOLDER_FULFILLED:
            return {
                ...state,
                items: state.items.map((item) => !item.newFolder
                    ? item
                    : {
                        ...item,
                        id: action.payload.data.id,
                        name: action.payload.data.name,
                        newFolder: false,
                        isLoading: false,
                    }
                ),
            };

        case SAVE_NEW_FOLDER_REJECTED:
            return {
                ...state,
                items: state.items.filter((i) => !i.newFolder),
            };

        case SAVE_NEW_FILE_PENDING:
            return {
                ...state,
                isLoadingNewFile: true,
            };

        case SAVE_NEW_FILE_REJECTED:
            return {
                ...state,
                isLoadingNewFile: false,
            };

        case SAVE_NEW_FILE_FULFILLED:
            return {
                ...state,
                isLoadingNewFile: false,
            };

        case SELECT_ITEM:
            const index = state.selectedItems.findIndex((item) => action.meta.id === item.id);

            return {
                ...state,
                selectedItems: index > -1
                    ? state.selectedItems.filter((item) => action.meta.id !== item.id)
                    : [...state.selectedItems, action.meta],
            };

        case SET_SELECTED_ITEMS:
            return {
                ...state,
                selectedItems: action.meta,
            };

        case UPLOADED_FILE:
            return {
                ...state,
                items: [action.meta, ...state.items],
            };

        case SET_RENAMING_ITEM:
            return {
                ...state,
                items: state.items.map((i) => i.id !== action.meta 
                    ? i
                    : { ...i, renaming: !i.renaming }),
            };

        case UPDATE_FOLDER_FULFILLED:
            if (action.meta != action.payload.data.parent.id) {
                return {
                    ...state,
                    items: state.items.filter((i) => i.id != action.payload.data.id),
                };
            }

            return {
                ...state,
                items: state.items.map((i) => i.id == action.payload.data.id
                    ? {
                        ...i,
                        name: action.payload.data.name,
                        renaming: false,
                    }
                    : i),
            };

        case UPDATE_FILE_FULFILLED:
            const file = action.payload.data;
            const parent = file.folder || file.parentReference || file.parent;

            if (action.meta != parent.id) {
                return {
                    ...state,
                    items: state.items.filter((i) => i.id != action.payload.data.id),
                };
            }

            return {
                ...state,
                items: state.items.map((i) => i.id == action.payload.data.id
                    ? {
                        ...i,
                        name: action.payload.data.name,
                        renaming: false,
                    }
                    : i),
            };

        case FETCH_SHAREPOINT_ATTACHED_FILES_FOR_PAGE_PENDING:
            return {
                ...state,
                sharePointAttachedFiles: {
                isLoading: true,
                searches: [],
            }
            };

        case FETCH_SHAREPOINT_ATTACHED_FILES_FOR_PAGE_FULFILLED:
            return {
                ...state,
                sharePointAttachedFiles: {
                    isLoading: false,
                    searches: action.payload.data,
                },
            };

        default:
            return state;
    }
};

export default fileReducer;
